// Alternative approach with HTML-based popup outside of OpenLayers
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  isValidExtent,
  MapContainer,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';

import { Feature } from 'ol';
import { LineString, Point } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Circle, Fill, Stroke, Style } from 'ol/style';
import { StyleFunction, StyleLike } from 'ol/style/Style';
import { transform } from 'ol/proj';
import { ShipTrackData } from './types';
import { BaseMap } from '../../../Map/BaseMap';
import { buffer as extentBuffer } from 'ol/extent';
import { cn } from '@ais-ui/utils';
import { HistoricalTrack } from '@ais-ui/api-spec/portserver';

interface ShipTrackProps {
  trackData: HistoricalTrack | null | undefined;
  hoveredPointIndex: number | null;
  onPointHover: (index: number | null) => void;
  dataType: 'sog' | 'cog';
  className?: string;
}

interface PointProperties {
  index: number;
  timestamp: string;
  course: number;
  speed: number;
}

interface PointFeature extends Feature {
  getProperties(): {
    properties: PointProperties;
  };
}

interface MapFeatures {
  trackFeatures: Feature[];
  pointFeatures: Feature[];
  extent: number[] | null;
}

function ShipTrackGeoJSON({
  trackData,
  hoveredPointIndex,
  onPointHover,
  dataType,
}: ShipTrackProps): JSX.Element {
  const { map, mapProjection, userProjection } = useOpenLayersContext();
  const pointsLayerRef = useRef<VectorLayer<Feature> | null>(null);
  const trackLayerRef = useRef<VectorLayer<Feature> | null>(null);
  const prevTrackDataRef = useRef<HistoricalTrack | null | undefined>(null);
  const mapContainerRef = useRef<HTMLElement | null>(null);

  // State for popup position and content
  const [popupInfo, setPopupInfo] = useState<{
    visible: boolean;
    x: number;
    y: number;
    speed: number;
    course: number;
    timestamp: string;
  }>({
    visible: false,
    x: 0,
    y: 0,
    speed: 0,
    course: 0,
    timestamp: '',
  });

  // Process track data into OpenLayers features
  const { trackFeatures, pointFeatures, extent } = useMemo((): MapFeatures => {
    if (!trackData?.track?.coordinates?.length) {
      return { trackFeatures: [], pointFeatures: [], extent: null };
    }

    // Create line feature for ship's path
    const lineCoordinates = trackData.track.coordinates.map((point: number[]) =>
      transform([point[0], point[1]], userProjection, mapProjection)
    );

    const lineStringFeature = new Feature({
      geometry: new LineString(lineCoordinates),
      name: 'Ship Track',
    });

    // Create point features for each position in the track
    const pointFeatures = trackData.track.coordinates.map(
      (point: number[], index: number) => {
        const transformedCoord = transform(
          [point[0], point[1]],
          userProjection,
          mapProjection
        );

        return new Feature({
          geometry: new Point(transformedCoord),
          properties: {
            index,
            timestamp: point[2],
            course: point[3],
            speed: point[4],
          },
        });
      }
    );

    // Calculate the extent of the track
    const trackGeometry = lineStringFeature.getGeometry() as LineString;
    const rawExtent = trackGeometry ? trackGeometry.getExtent() : null;

    // Buffer the extent to add some padding around the track
    const bufferedExtent = rawExtent
      ? extentBuffer(rawExtent, rawExtent[2] * 0.1)
      : null;

    return {
      trackFeatures: [lineStringFeature],
      pointFeatures,
      extent: bufferedExtent,
    };
  }, [trackData, mapProjection, userProjection]);

  // Style for the track line
  const trackStyle = new Style({
    stroke: new Stroke({
      color: 'rgba(0, 100, 122, 0.7)',
      width: 3,
    }),
  });

  // Style function for points with hover effect
  const getPointStyle = useCallback(
    (feature: Feature): Style | StyleLike => {
      const properties = (feature as PointFeature).getProperties().properties;
      const isHovered = properties.index === hoveredPointIndex;

      if (isHovered) {
        return new Style({
          image: new Circle({
            radius: 8,
            fill: new Fill({ color: 'rgba(0, 163, 196, 0.8)' }),
            stroke: new Stroke({ color: 'white', width: 2 }),
          }),
          zIndex: 100,
        });
      }

      return new Style({
        image: new Circle({
          radius: 4,
          fill: new Fill({ color: 'rgba(0, 100, 122, 0.7)' }),
          stroke: new Stroke({ color: 'white', width: 1 }),
        }),
        zIndex: 10,
      });
    },
    [hoveredPointIndex]
  );

  // Check if track data has changed
  const hasTrackDataChanged = useCallback((): boolean => {
    if (!prevTrackDataRef.current && !trackData) return false;
    if (!prevTrackDataRef.current || !trackData) return true;

    // Simple comparison - could be enhanced for more specific change detection
    const prevCoords = prevTrackDataRef.current.track?.coordinates?.length;
    const currentCoords = trackData.track?.coordinates?.length;

    return prevCoords !== currentCoords;
  }, [trackData]);

  // Set up OpenLayers layers and interactions
  useEffect(() => {
    if (!map) return;

    // Store a reference to the map container for popup positioning
    mapContainerRef.current = map.getTargetElement();

    // Create sources and layers
    const trackSource = new VectorSource({ features: trackFeatures });
    const pointSource = new VectorSource({ features: pointFeatures });

    const trackLayer = new VectorLayer({
      source: trackSource,
      style: trackStyle,
      zIndex: 10,
    });

    // Fixed typing for the VectorLayer style property
    const pointsLayer = new VectorLayer({
      source: pointSource,
      style: getPointStyle as StyleFunction,
      zIndex: 11,
    });

    pointsLayerRef.current = pointsLayer;
    trackLayerRef.current = trackLayer;

    // Add layers to map
    map.addLayer(trackLayer);
    map.addLayer(pointsLayer);

    // Only zoom to extent if track data has changed
    const dataChanged = hasTrackDataChanged();

    if (extent && isValidExtent(extent) && dataChanged) {
      setTimeout(() => {
        map.getView().fit(extent, {
          padding: [50, 50, 50, 50], // Add padding around the extent
          duration: 500, // Smooth animation
        });
      }, 100);
    }

    // Update the reference to current track data
    prevTrackDataRef.current = trackData;

    // Set up hover interaction
    const handlePointerMove = (e: any) => {
      const pixel = map.getEventPixel(e.originalEvent);
      const hit = map.hasFeatureAtPixel(pixel, {
        layerFilter: (layer) => layer === pointsLayer,
        hitTolerance: 16,
      });

      if (hit) {
        map.getTargetElement().style.cursor = 'pointer';
        const features = map.getFeaturesAtPixel(pixel, {
          layerFilter: (layer) => layer === pointsLayer,
          hitTolerance: 16,
        });

        if (features?.length) {
          const feature = features[0] as PointFeature;
          const properties = feature.getProperties().properties;
          const index = properties.index;

          // Get screen coordinates for the popup
          const coordinate = map.getCoordinateFromPixel(pixel);
          const pixelPosition = map.getPixelFromCoordinate(coordinate);

          // Update popup state with position and data
          if (pixelPosition) {
            setPopupInfo({
              visible: true,
              x: pixelPosition[0],
              y: pixelPosition[1],
              speed: properties.speed,
              course: properties.course,
              timestamp: properties.timestamp,
            });
          }

          onPointHover(index);
        }
      } else {
        map.getTargetElement().style.cursor = '';
        setPopupInfo((prev) => ({ ...prev, visible: false }));
        onPointHover(null);
      }
    };

    map.on('pointermove', handlePointerMove);

    // Cleanup function
    return () => {
      map.removeLayer(trackLayer);
      map.removeLayer(pointsLayer);
      map.un('pointermove', handlePointerMove);
      pointsLayerRef.current = null;
      trackLayerRef.current = null;
    };
  }, [
    map,
    trackFeatures,
    pointFeatures,
    trackStyle,
    getPointStyle,
    onPointHover,
    extent,
    hasTrackDataChanged,
    trackData,
  ]);

  // Update layer styles and popup when hoveredPointIndex changes
  useEffect(() => {
    if (!map || !pointsLayerRef.current) return;

    pointsLayerRef.current.changed();

    // Update popup when hoveredPointIndex changes (for programmatic hover)
    if (
      hoveredPointIndex !== null &&
      pointFeatures.length > hoveredPointIndex
    ) {
      const feature = pointFeatures[hoveredPointIndex];
      const properties = (feature as PointFeature).getProperties().properties;

      // Convert map coordinates to screen coordinates for popup
      const geometry = feature.getGeometry() as Point;
      const coordinate = geometry.getCoordinates();
      const pixelPosition = map.getPixelFromCoordinate(coordinate);

      if (pixelPosition) {
        setPopupInfo({
          visible: true,
          x: pixelPosition[0],
          y: pixelPosition[1],
          speed: properties.speed,
          course: properties.course,
          timestamp: properties.timestamp,
        });
      }
    } else if (hoveredPointIndex === null) {
      // Hide popup when no point is hovered
      setPopupInfo((prev) => ({ ...prev, visible: false }));
    }
  }, [hoveredPointIndex, pointFeatures, map]);

  // Render the popup outside of OpenLayers as a React component
  return (
    <>
      {popupInfo.visible && (
        <div
          style={{
            position: 'absolute',
            left: `${popupInfo.x}px`,
            top: `${popupInfo.y - 20}px`, // Offset above the point
            transform: 'translate(-50%, -100%)', // Center horizontally and position above
            backgroundColor: 'white',
            padding: '5px 10px',
            borderRadius: '0.5rem',
            boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
            border: '1px solid #cccccc',
            minWidth: '100px',
            zIndex: 1000,
            fontSize: '12px',
            pointerEvents: 'none', // Don't interfere with map interaction
          }}
        >
          <div>
            <strong>
              {dataType === 'sog'
                ? `${popupInfo.speed.toFixed(1)} knots`
                : `${popupInfo.course.toFixed(1)}°`}
            </strong>
            <br />
            <span>{popupInfo.timestamp}</span>
          </div>
          {/* Arrow pointing down */}
          <div
            style={{
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              marginLeft: '-10px',
              borderWidth: '10px 10px 0',
              borderStyle: 'solid',
              borderColor: 'white transparent transparent',
            }}
          />
        </div>
      )}
    </>
  );
}

export default function ShipTrackMap({
  trackData,
  hoveredPointIndex,
  onPointHover,
  dataType,
  className,
}: ShipTrackProps) {
  // Calculate map center based on track data (as a fallback)
  const mapCenter = useMemo((): [number, number] => {
    if (!trackData?.track?.coordinates?.length) {
      return [11, 59]; // Default center if no data
    }

    // Calculate average position of all coordinates
    const coordinates = trackData.track.coordinates;
    const sumLon: number = coordinates.reduce(
      (sum: number, point: number[]) => sum + point[0],
      0
    );
    const sumLat: number = coordinates.reduce(
      (sum: number, point: number[]) => sum + point[1],
      0
    );
    const count = coordinates.length;

    return [sumLon / count, sumLat / count];
  }, [trackData]);

  return (
    <div
      className={cn(
        'h-[500px] rounded-lg overflow-hidden shadow-md relative',
        className
      )}
    >
      <MapContainer center={mapCenter} zoom={10}>
        <BaseMap />
        <ShipTrackGeoJSON
          trackData={trackData}
          hoveredPointIndex={hoveredPointIndex}
          onPointHover={onPointHover}
          dataType={dataType}
        />
      </MapContainer>
    </div>
  );
}
